// Server-driven config for the homepage "Top Sellers" promo hero.
// Mirrors services/remoteConfig.js: module singleton, localStorage TTL cache,
// in-flight de-dupe, and a graceful default so the app works if the API is down.
// Region-aware (cache key + fetch include the resolved countryCode).

const CACHE_PREFIX = 'promoConfig';
const DEFAULT_CACHE_DURATION_MS = 10 * 60 * 1000; // 10 min fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const cacheKeyFor = (region) => `${CACHE_PREFIX}:${region || 'GLOBAL'}`;

class PromoConfigManager {
    constructor() {
        this.config = null;
        this.loadPromise = null;
        this.cacheDurationMs = DEFAULT_CACHE_DURATION_MS;
    }

    // The offline / no-campaign default — reproduces today's hardcoded Top Sellers behaviour.
    getDefaultConfig() {
        return {
            enabled: true,
            theme: 'default',
            title: null,
            subtitle: null,
            discountPercent: 30,
            minGames: 3,
            backgroundImageUrl: null,
            backgroundColour: null,
            textColour: null,
            accentColour: null,
            gameIds: [],
            filterTag: 'best-seller',
            maxGames: 4,
            // Default tabs match the historical homepage (filtered by the game-show / party-game tags).
            tabs: [
                { label: 'Game Shows', tag: 'game-show' },
                { label: 'Party Games', tag: 'party-game' },
            ],
            // Shop rewards banner (null subtitle => derived from the discount in the component).
            bannerRibbonText: 'PERFECT PARTY NIGHT',
            bannerTitle: 'Buy TV Party Games — Play Tonight on Your Big Screen',
            bannerSubtitle: null,
        };
    }

    // Map the API DTO into the stable config shape the hero/checkout read.
    normalize(data) {
        const def = this.getDefaultConfig();
        if (!data || data.hasCampaign === false) {
            return def;
        }
        // Mirror the server's checkout rule (StripeController): a campaign only
        // grants the auto-discount when its DiscountPercent > 0 — null/0 means
        // NO discount, not "use the default". Falling back to 30% here would
        // advertise a discount the payment intent won't actually apply.
        const pct = Number(data.discountPercent);
        const hasDiscount = Number.isFinite(pct) && pct > 0;
        return {
            enabled: true,
            theme: data.theme || def.theme,
            title: data.title ?? null,
            subtitle: data.subtitle ?? null,
            discountPercent: hasDiscount ? pct : null,
            minGames: hasDiscount ? (Number(data.minGames) > 0 ? Number(data.minGames) : def.minGames) : null,
            backgroundImageUrl: data.backgroundImageUrl ?? null,
            backgroundColour: data.backgroundColour ?? null,
            textColour: data.textColour ?? null,
            accentColour: data.accentColour ?? null,
            gameIds: Array.isArray(data.gameIds) ? data.gameIds : [],
            filterTag: data.filterTag || def.filterTag,
            maxGames: data.maxGames ?? def.maxGames,
            // Server is authoritative for tabs: a campaign with no tabs => single combined list.
            tabs: Array.isArray(data.tabs)
                ? data.tabs.filter(t => t && t.tag && t.label)
                : [],
            bannerRibbonText: data.bannerRibbonText || def.bannerRibbonText,
            bannerTitle: data.bannerTitle || def.bannerTitle,
            bannerSubtitle: data.bannerSubtitle || null,
        };
    }

    getCachedConfig(region) {
        try {
            const cached = localStorage.getItem(cacheKeyFor(region));
            if (!cached) return null;
            const { data, timestamp, ttl } = JSON.parse(cached);
            const cacheDuration = ttl || DEFAULT_CACHE_DURATION_MS;
            if (Date.now() - timestamp < cacheDuration) {
                this.cacheDurationMs = cacheDuration;
                return data;
            }
            localStorage.removeItem(cacheKeyFor(region));
            return null;
        } catch (error) {
            console.error('Error reading cached promo config:', error);
            return null;
        }
    }

    setCachedConfig(region, data) {
        try {
            localStorage.setItem(cacheKeyFor(region), JSON.stringify({
                data,
                timestamp: Date.now(),
                ttl: this.cacheDurationMs,
            }));
        } catch (error) {
            console.error('Error caching promo config:', error);
        }
    }

    async fetchFromAPI(region) {
        const regionParam = region ? `?region=${encodeURIComponent(region)}` : '';
        const response = await fetch(`${API_BASE_URL}api/public/promo-section${regionParam}`);
        if (!response.ok) {
            throw new Error('Failed to fetch promo config');
        }
        const data = await response.json();
        if (data.cacheTtlSeconds) {
            this.cacheDurationMs = data.cacheTtlSeconds * 1000;
        }
        return this.normalize(data);
    }

    async load(region) {
        if (this.loadPromise) {
            return this.loadPromise;
        }

        const cached = this.getCachedConfig(region);
        if (cached) {
            this.config = cached;
            return cached;
        }

        this.loadPromise = this.fetchFromAPI(region)
            .then(data => {
                this.config = data;
                this.setCachedConfig(region, data);
                this.loadPromise = null;
                return data;
            })
            .catch(error => {
                this.loadPromise = null;
                console.error('Failed to load promo config, using defaults');
                this.config = this.getDefaultConfig();
                return this.config;
            });

        return this.loadPromise;
    }

    // Force a fresh fetch (e.g. when the resolved countryCode changes).
    async refetch(region) {
        this.loadPromise = null;
        try {
            const data = await this.fetchFromAPI(region);
            this.config = data;
            this.setCachedConfig(region, data);
            return data;
        } catch (error) {
            console.error('Failed to refetch promo config, using defaults');
            this.config = this.getDefaultConfig();
            return this.config;
        }
    }

    getConfig() {
        return this.config || this.getDefaultConfig();
    }
}

// Export a singleton instance
export const promoConfig = new PromoConfigManager();
